import React, { useEffect, useState, useCallback } from 'react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { formatDateTime } from '../../components/admin/AdminUI';
import { Button } from '../../components/ui/button';
import { Download } from 'lucide-react';

export const AdminSubscribers = () => {
  const { authedAxios } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await authedAxios().get('/api/admin/subscribers');
      setItems(data.subscribers || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [authedAxios]);

  useEffect(() => {
    load();
  }, [load]);

  const exportCSV = () => {
    const rows = [['Email', 'Subscribed At', 'Active']];
    items.forEach((s) => rows.push([s.email, s.subscribed_at || '', s.active ? 'Yes' : 'No']));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `creek-bottom-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <p className="text-[#F4F1EA]/60">Loading subscribers...</p>;
  if (err) return <p className="text-red-400">{err}</p>;

  return (
    <div data-testid="admin-subscribers-page" className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-3xl font-bold text-[#F4F1EA]">Newsletter Subscribers</h2>
          <p className="text-[#F4F1EA]/60 mt-1">{items.length} total</p>
        </div>
        <Button
          data-testid="admin-export-subscribers-btn"
          onClick={exportCSV}
          disabled={items.length === 0}
          className="bg-[#B8860B] hover:bg-[#B8860B]/90 text-[#050814] font-semibold"
        >
          <Download className="mr-2 h-4 w-4" /> Export CSV
        </Button>
      </div>

      <div className="overflow-x-auto bg-[#0B132B] border border-[#B8860B]/30 rounded-lg">
        <table className="w-full text-sm">
          <thead className="bg-[#050814] text-[#B8860B] uppercase tracking-wider text-xs">
            <tr>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Subscribed</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="3" className="p-6 text-center text-[#F4F1EA]/60">
                  No subscribers yet.
                </td>
              </tr>
            )}
            {items.map((s) => (
              <tr
                key={s.id}
                data-testid={`admin-subscriber-${s.id}`}
                className="border-t border-[#B8860B]/10 hover:bg-[#050814]/60"
              >
                <td className="p-3 text-[#F4F1EA]">{s.email}</td>
                <td className="p-3 text-[#F4F1EA]/85">{formatDateTime(s.subscribed_at)}</td>
                <td className="p-3">
                  <span
                    className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${
                      s.active
                        ? 'bg-[#B8860B] text-[#F4F1EA]'
                        : 'bg-red-900/40 text-red-300 border border-red-500/40'
                    }`}
                  >
                    {s.active ? 'Active' : 'Unsubscribed'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSubscribers;
